import React, { useState, useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInstagram, faDiscord, faWhatsapp, faApple, faCcVisa, faCcMastercard, faCcAmex, faCcDiscover, faBitcoin, faEthereum } from '@fortawesome/free-brands-svg-icons';
import { faShieldAlt } from '@fortawesome/free-solid-svg-icons';
import '../styles/Footer.scss';
import '../styles/Footer.css';
import { BASE_URL } from '../utils/SEOUtils';

// This would normally be fetched from an API, but for now we'll use a constant
// In a real implementation, you would connect to Trustpilot's API
const TRUSTPILOT_DATA = {
  rating: 4.7,
  reviewCount: 23,
  url: 'https://www.trustpilot.com/review/homeworkguy.live'
};

const Footer: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isOrderPage = location.pathname === '/order' || location.pathname === 'order';
  
  // State for Trustpilot data
  const [trustpilotData, setTrustpilotData] = useState(TRUSTPILOT_DATA);
  
  // In a real implementation, you would fetch from Trustpilot's API here
  useEffect(() => {
    // This is where you would implement the API call to get live data
    // For now, we'll just use the constant data
    
    // Example of how this would be implemented:
    // const fetchTrustpilotData = async () => {
    //   try {
    //     const response = await fetch('https://api.trustpilot.com/v1/business-units/homeworkguy.org/reviews');
    //     const data = await response.json();
    //     setTrustpilotData({
    //       rating: data.summary.stars,
    //       reviewCount: data.summary.total,
    //       url: 'https://www.trustpilot.com/review/homeworkguy.live'
    //     });
    //   } catch (error) {
    //     console.error("Error fetching Trustpilot data:", error);
    //   }
    // };
    // 
    // fetchTrustpilotData();
  }, []);
  
  // Force navigation from Order page
  const handleNavigation = (path: string, e: React.MouseEvent) => {
    if (isOrderPage) {
      e.preventDefault();
      
      // Clean up Order page styles
      document.body.classList.remove('order-page-active');
      document.body.classList.remove('textarea-focused');
      document.body.classList.remove('no-scroll');
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      
      // Reset scroll position
      window.scrollTo(0, 0);
      
      // Navigate to the target path
      // Handle root path specially
      const baseUrl = window.location.origin;
      // If path is '/' set it to empty string for the correct root URL
      const cleanPath = path === '/' ? '' : path.startsWith('/') ? path.substring(1) : path;
      const targetPath = `${baseUrl}/${cleanPath}`;
      
      console.log(`Footer: Navigating from Order page to ${targetPath} using window.location`);
      
      // Use window.location for most reliable navigation
      window.location.href = targetPath;
      
      return true;
    }
    return false;
  };
  
  return (
    <footer className="footer">
      <Container>
        <Row>
          <Col md={4}>
            <h5>About Us</h5>
            <p>
              We are dedicated to helping students achieve academic excellence through our professional online class taking and homework help services.
            </p>
          </Col>
          <Col md={4}>
            <h5>Policies</h5>
            <ul className="list-unstyled">
              <li>
                <a 
                  href={`${BASE_URL}/`}
                  onClick={(e) => isOrderPage && handleNavigation('/', e)}
                >
                  Home
                </a>
              </li>
              <li>
                <Link 
                  to="terms-conditions"
                  onClick={(e) => isOrderPage && handleNavigation('terms-conditions', e)}
                >
                  Terms & Conditions
                </Link>
              </li>
              <li>
                <Link 
                  to="privacy-policy"
                  onClick={(e) => isOrderPage && handleNavigation('privacy-policy', e)}
                >
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link 
                  to="cookie-policy"
                  onClick={(e) => isOrderPage && handleNavigation('cookie-policy', e)}
                >
                  Cookie Policy
                </Link>
              </li>
              <li>
                <Link 
                  to="refund-guarantee"
                  onClick={(e) => isOrderPage && handleNavigation('refund-guarantee', e)}
                >
                  Refund Guarantee
                </Link>
              </li>
              <li>
                <Link 
                  to="faq"
                  onClick={(e) => isOrderPage && handleNavigation('faq', e)}
                >
                  FAQ
                </Link>
              </li>
            </ul>
          </Col>
          <Col md={4}>
            <h5>Contact Info</h5>
            <ul className="list-unstyled contact-list">
              <li>
                <span className="contact-label">Email:</span> 
                <a href="mailto:support@homeworkguy.live" className="contact-link"> support@homeworkguy.live</a>
              </li>
              <li>
                <span className="contact-label">Phone & WhatsApp:</span> 
                <a href="tel:+14087094522" className="contact-link">+1 (408) 709-4522</a>
              </li>
              <li>
                <span className="contact-label">Hours:</span> 24/7 Support Available
              </li>
            </ul>
            
            <div className="social-media-icons">
              <a href="https://instagram.com/homework_guy_official" target="_blank" rel="noopener noreferrer" aria-label="Visit our Instagram page">
                <span className="contact-icon-circle instagram-bg">
                  <FontAwesomeIcon icon={faInstagram} />
                </span>
              </a>
              <a href="https://wa.me/+14087094522" target="_blank" rel="noopener noreferrer" aria-label="Contact us via WhatsApp">
                <span className="contact-icon-circle whatsapp-bg">
                  <FontAwesomeIcon icon={faWhatsapp} />
                </span>
              </a>
              <a href="https://discord.gg/6Td5S9xuhr" target="_blank" rel="noopener noreferrer" aria-label="Join our Discord community">
                <span className="contact-icon-circle discord-bg">
                  <FontAwesomeIcon icon={faDiscord} />
                </span>
              </a>
            </div>
          </Col>
        </Row>
        <Row className="mt-4">
          <Col className="text-center">
            <div className="trustpilot-container mb-3">
              <a href={trustpilotData.url} target="_blank" rel="noopener noreferrer" className="trustpilot-badge">
                <div className="trustpilot-logo">
                  <svg height="20" width="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20.99 7.8l-7.3-1.06-3.27-6.64-3.28 6.64L0 7.8l5.3 5.17-1.25 7.3 6.63-3.48 6.63 3.48-1.25-7.3L20.99 7.8z" fill="#00B67A" />
                  </svg>
                </div>
                <span className="trustpilot-text">Trustpilot</span>
                <div className="stars">
                  <span className="stars-green">★★★★★</span>
                </div>
                <span className="trustpilot-rating">{trustpilotData.rating}</span>
              </a>
            </div>

            <div className="badges-container mb-3">
              <div className="badges-row">
                <div className="dmca-badge">
                  <div className="dmca-container">
                    <div className="dmca-green">DMCA</div>
                    <div className="dmca-dark">PROTECTED</div>
                  </div>
                </div>
                
                <div className="money-back-badge">
                  <Link to="/refund-guarantee" className="badge-link">
                    <div className="money-back-container">
                      <FontAwesomeIcon icon={faShieldAlt} className="shield-icon" />
                      <span className="guarantee-text">MONEY BACK GUARANTEE</span>
                    </div>
                  </Link>
                </div>
                
                <div className="crypto-badge">
                  <div className="crypto-container">
                    <FontAwesomeIcon icon={faBitcoin} className="crypto-icon bitcoin-icon" />
                    <FontAwesomeIcon icon={faEthereum} className="crypto-icon ethereum-icon" />
                    <span className="crypto-text">CRYPTOCURRENCY ACCEPTED</span>
                  </div>
                </div>
              </div>
              
              <h6 className="payment-title mt-3 mb-2">Payment Methods Accepted</h6>
              <div className="payment-methods">
                <FontAwesomeIcon icon={faCcVisa} className="payment-icon" />
                <FontAwesomeIcon icon={faCcMastercard} className="payment-icon" />
                <FontAwesomeIcon icon={faCcAmex} className="payment-icon" />
                <FontAwesomeIcon icon={faCcDiscover} className="payment-icon" />
                <FontAwesomeIcon icon={faApple} className="payment-icon" />
              </div>
            </div>
            
            <p className="copyright mb-0">&copy; {new Date().getFullYear()} Homework Guy. All rights reserved.</p>
            <div className="disclaimer mt-3">
              <p className="disclaimer-text small text-muted">
                Disclaimer: Homework Guy provides academic assistance for reference and learning purposes only. 
                All work delivered is intended to be used as a guidance tool and should not be submitted as your own work. 
                Students are responsible for properly citing any content provided by our service.
              </p>
            </div>
          </Col>
        </Row>
      </Container>
    </footer>
  );
};

export default Footer; 